/**
 * Configuration object used when loading a new stream
 */
import { SubtitleTrack } from './SubtitleTrack';
import { LabelConfig } from './LabelConfig';

export class MKStream {
  private readonly _assetIdentifier: string;
  private readonly _primaryUrl?: string;
  private readonly _secondaryUrl?: string;
  private readonly _subtitleTracks: SubtitleTrack[];
  private readonly _cdnFailoverPercent: number = 0;
  private readonly _poster: string;
  private readonly _userId: string;
  private readonly _customData1: string;
  private readonly _customData2: string;
  private readonly _customData3: string;
  private readonly _customData4: string;
  private readonly _customData5: string;
  private readonly _labelConfig: LabelConfig;
  private _didFailOver: boolean = false;

  /**
   *
   * @param primaryUrl - primary stream url
   * @param secondaryUrl - secondary backup stream url
   * @param subtitleTracks - a list of external subtitle tracks that should be loaded with the player
   * @param poster - url to a poster image to render on the player
   * @param cdnFailoverPercent - percent value from 0-100 describing what percentage of users should initially playback
   *   the secondary url
   * @param assetIdentifier - identifier for the current stream
   * @param labelConfig - config to change the labels in the player dropdown
   */
  constructor(primaryUrl: string, assetIdentifier: string, subtitleTracks?: SubtitleTrack[], poster?: string, secondaryUrl?: string, cdnFailoverPercent?: number, userId?: string, customData1?: string, customData2?: string, customData3?: string, customData4?: string, customData5?: string, labelConfig?: LabelConfig) {
    this._cdnFailoverPercent = cdnFailoverPercent;
    this._secondaryUrl = secondaryUrl;
    this._primaryUrl = primaryUrl;
    this._assetIdentifier = assetIdentifier;
    this._subtitleTracks = subtitleTracks;
    this._poster = poster;
    this._userId = userId;
    this._customData1 = customData1;
    this._customData2 = customData2;
    this._customData3 = customData3;
    this._customData4 = customData4;
    this._customData5 = customData5;
    this._labelConfig = labelConfig;
  }

  /**
   * percent value from 0-100 describing what percentage of users should playback the secondary url
   */
  public get cdnFailoverPercent(): number {
    return this._cdnFailoverPercent;
  }

  /**
   * primary stream url used during playback
   */
  public get primaryUrl(): string {
    return this._primaryUrl;
  }

  /**
   * secondary stream url used during playback
   */
  public get secondaryUrl(): string {
    return this._secondaryUrl;
  }

  /**
   * percent value from 0-100 describing what percentage of users should playback the secondary url
   */
  public get subtitleTracks(): SubtitleTrack[] {
    return this._subtitleTracks;
  }

  /**
   * identifier for the current stream
  */
  public get assetIdentifier(): string {
    return this._assetIdentifier;
  }

  /**
   * poster art for the current stream
   */
  public get poster(): string {
    return this._poster;
  }

  /**
   * customData1 field for the current stream
   */
  public get customData1(): string {
    return this._customData1;
  }

  /**
   * customData2 field for the current stream
   */
  public get customData2(): string {
    return this._customData2;
  }

  /**
   * customData3 field for the current stream
   */
  public get customData3(): string {
    return this._customData3;
  }

  /**
   * customData5 field for the current stream
   */
  public get customData4(): string {
    return this._customData4;
  }

  /**
   * customData5 field for the current stream
   */
  public get customData5(): string {
    return this._customData5;
  }

  /**
   * userId field for the current stream
   */
  public get userId(): string {
    return this._userId;
  }

  /**
   * config to change the labels in the player dropdown
   */
  public get labelConfig(): LabelConfig {
    return this._labelConfig;
  }

  /**
   * boolean value describing if failover happened
   */
  public get didFailOver(): boolean {
    return this._didFailOver;
  }

  /**
   * setter for the didFailOver boolean value
   */
  public set didFailOver(value: boolean) {
    this._didFailOver = value;
  }
}